import React from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";

const EFFECTIVE_DATE = "September 1, 2026";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#FAFBFD]">
      <Nav />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14">
        <Link to="/" className="text-sm text-slate-500 hover:text-slate-900" data-testid="privacy-back-link">← Back to home</Link>
        <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-4">Privacy Policy</h1>
        <p className="text-sm text-slate-500 mt-2">Effective: {EFFECTIVE_DATE}</p>

        <div className="prose prose-slate max-w-none mt-8 space-y-6 text-slate-700 text-[15px] leading-relaxed">

          <section>
            <h2 className="text-xl font-semibold text-slate-900">1. Information We Collect</h2>
            <p>We collect only what we need to help you draft refund messages:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Account:</strong> your email, name, and hashed password (we never store plaintext passwords).</li>
              <li><strong>Case details:</strong> merchant name, subscription ID, amount charged, cancel date, charge date, account email, brief description you write.</li>
              <li><strong>Uploads:</strong> screenshots you attach (stored on Emergent Object Storage, visible only to you).</li>
              <li><strong>Payments:</strong> handled by Stripe. We store only the Stripe session/payment IDs — never your full card details.</li>
              <li><strong>Usage:</strong> case status, timestamps, referral codes.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">2. How We Use It</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>To generate your personalized refund messages.</li>
              <li>To send you transactional emails (receipts, case reminders) if you enable email.</li>
              <li>To process your $19 payment via Stripe.</li>
              <li>To compute aggregate stats (total recovered, resolved cases) for our public landing page — always anonymized (first name + last initial only).</li>
              <li>To operate the referral program.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">3. AI Processing</h2>
            <p>To generate the 1-2 sentence paraphrase in each message, we send the merchant name, dates, amount, and your typed issue-details to Anthropic (Claude). Anthropic does not train on our API traffic. We do not send your email, name, screenshots, or payment details to the AI.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">4. Who We Share Data With</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Stripe</strong> — for payment processing.</li>
              <li><strong>Anthropic</strong> — for AI paraphrase generation.</li>
              <li><strong>Emergent</strong> — our hosting and object storage provider.</li>
              <li><strong>MongoDB Atlas</strong> — our database provider.</li>
            </ul>
            <p>We do <strong>not</strong> sell your data. We do <strong>not</strong> share your case details with the merchants — only you do that, by copying and pasting our messages.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">5. What Shows Up Publicly</h2>
            <p>The public landing page displays aggregate stats (total recovered, case counts) and a "wins feed" using only your <em>first name + last initial</em>, the merchant name, amount, and days to resolve. Never your email, subscription ID, or full name.</p>
            <p>The Founder Wall displays your first name + last initial + founding number. You can request removal by emailing support.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">6. Security</h2>
            <p>Passwords are hashed with bcrypt. All traffic runs over HTTPS. Screenshots are served through authenticated URLs — only you can view your own uploads. Payment card data never touches our servers.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">7. Data Retention</h2>
            <p>We keep case data and screenshots for as long as your account is active. You may delete individual attachments at any time from your case page. If you close your account, we delete your data within 30 days, except records we must retain for tax or legal purposes (e.g., Stripe payment records).</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">8. Your Rights (US Users)</h2>
            <p>If you are a California resident, you have the right to know what personal information we hold, request deletion, and opt out of any sale of your information (we do not sell). Email <a href="mailto:privacy@powerhouserefunds.com" className="text-[#1E3A8A] hover:underline">privacy@powerhouserefunds.com</a> to exercise these rights.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">9. Cookies</h2>
            <p>We use a single browser storage item (your login token) so you don't have to re-login constantly. We do not use third-party advertising cookies or trackers.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">10. Children</h2>
            <p>The Service is not intended for anyone under 18. We do not knowingly collect information from minors.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">11. Changes</h2>
            <p>We'll update the "Effective" date at the top of this page when we make material changes. Continued use after changes means acceptance.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900">12. Contact</h2>
            <p>Questions or requests? Email <a href="mailto:privacy@powerhouserefunds.com" className="text-[#1E3A8A] hover:underline">privacy@powerhouserefunds.com</a>.</p>
          </section>

        </div>

        <div className="mt-10 text-center">
          <Link to="/"><Button variant="outline" className="rounded-full">Back to home</Button></Link>
        </div>
      </div>
    </div>
  );
}
